import type { AttachmentRef } from '@safewallet/core';
import type { PowerSyncDatabase } from '@powersync/web';
import {
  deleteAttachmentDeletion,
  deleteLocalAttachment,
  listAttachmentDeletions,
  listLocalAttachments,
  loadLocalAttachment,
  queueAttachmentDeletion,
  saveLocalAttachment,
  updateAttachmentDeletion,
  updateLocalAttachment,
  type AttachmentDeletionRequest,
  type LocalEncryptedAttachment,
} from './attachment-store';
import type { DeviceState } from './device-store';
import { currentAccessToken, supabase } from './supabase';
import { syncLog } from './debug-log';

const signerEndpoint = import.meta.env.VITE_R2_SIGNER_URL?.trim().replace(/\/+$/g, '');
export const r2SyncEnabled = import.meta.env.VITE_ENABLE_R2_SYNC === 'true' && Boolean(signerEndpoint);

interface AttachmentSyncContext {
  database: PowerSyncDatabase;
  device: Pick<DeviceState, 'accountUserId' | 'deviceId' | 'deviceAuthToken' | 'vaultId'>;
}

interface AttachmentMetadataRow {
  id: string;
  vault_id: string;
  record_id: string;
  crypto_version: number;
  object_key: string;
  thumbnail_object_key: string | null;
  wrapped_attachment_key: string;
  key_nonce: string;
  original_nonce: string;
  thumbnail_nonce: string | null;
  ciphertext_size: number;
  thumbnail_ciphertext_size: number | null;
  checksum: string;
  thumbnail_checksum: string | null;
  mime_type: string;
  state: 'pending' | 'uploaded' | 'failed' | 'deleted' | 'purged';
}

type AttachmentOperation = 'put' | 'get' | 'head' | 'delete';
type AttachmentVariant = 'original' | 'thumbnail';

interface SignedOperation {
  url: string;
  method: 'PUT' | 'GET' | 'HEAD' | 'DELETE';
  expiresInSeconds: number;
  expectedSize: number | null;
  expectedChecksum: string | null;
  contentType: string;
  headers?: Record<string, string>;
}

export interface AttachmentSyncSummary {
  uploaded: number;
  deleted: number;
  failed: number;
}

let context: AttachmentSyncContext | null = null;
let queuePromise: Promise<AttachmentSyncSummary> | null = null;

export function configureAttachmentSync(
  database: PowerSyncDatabase,
  device: Pick<DeviceState, 'accountUserId' | 'deviceId' | 'deviceAuthToken' | 'vaultId'>,
): void {
  context = r2SyncEnabled && device.accountUserId ? { database, device } : null;
}

export function clearAttachmentSyncContext(): void {
  context = null;
}

function errorMessage(value: unknown): string {
  if (value instanceof Error) return value.message;
  if (typeof value === 'string') return value;
  return 'Attachment synchronization failed.';
}

async function signedOperation(
  attachmentId: string,
  operation: AttachmentOperation,
  variant: AttachmentVariant,
): Promise<SignedOperation> {
  if (!context || !signerEndpoint) throw new Error('Secure attachment backup is not configured.');
  const token = await currentAccessToken();
  if (!token) throw new Error('Sign in to back up encrypted attachments.');

  const response = await fetch(signerEndpoint, {
    method: 'POST',
    headers: {
      authorization: `Bearer ${token}`,
      'content-type': 'application/json',
      'x-safe-wallet-device-id': context.device.deviceId,
      'x-safe-wallet-device-token': context.device.deviceAuthToken,
    },
    body: JSON.stringify({ attachmentId, operation, variant }),
  });
  const result = await response.json().catch(() => ({})) as Partial<SignedOperation> & { error?: string };
  if (!response.ok || !result.url || !result.method) {
    throw new Error(result.error || `Attachment authorization failed (${response.status}).`);
  }
  return result as SignedOperation;
}

async function sha256Hex(bytes: ArrayBuffer): Promise<string> {
  const digest = new Uint8Array(await crypto.subtle.digest('SHA-256', bytes));
  return [...digest].map((value) => value.toString(16).padStart(2, '0')).join('');
}

function transferHeaders(signed: SignedOperation): Headers {
  const headers = new Headers(signed.headers ?? {});
  if (signed.method === 'PUT' && !headers.has('content-type')) {
    headers.set('content-type', signed.contentType || 'application/octet-stream');
  }
  return headers;
}

async function loadAttachmentMetadata(attachmentId: string): Promise<AttachmentMetadataRow | null> {
  if (!context) return null;
  const local = await context.database.getOptional<AttachmentMetadataRow>(
    `SELECT id, vault_id, record_id, crypto_version, object_key, thumbnail_object_key,
      wrapped_attachment_key, key_nonce, original_nonce, thumbnail_nonce,
      ciphertext_size, thumbnail_ciphertext_size, checksum, thumbnail_checksum,
      mime_type, state
     FROM attachments WHERE id = ?`,
    [attachmentId],
  );
  if (local) return local;
  if (!supabase) return null;

  const { data, error } = await supabase
    .from('attachments')
    .select('id,vault_id,record_id,crypto_version,object_key,thumbnail_object_key,wrapped_attachment_key,key_nonce,original_nonce,thumbnail_nonce,ciphertext_size,thumbnail_ciphertext_size,checksum,thumbnail_checksum,mime_type,state')
    .eq('id', attachmentId)
    .maybeSingle();
  if (error) throw error;
  return data as AttachmentMetadataRow | null;
}

async function createAttachmentMetadata(local: LocalEncryptedAttachment): Promise<AttachmentMetadataRow | null> {
  if (!context || !supabase) throw new Error('Cloud synchronization is not configured.');
  const { data, error } = await supabase.rpc('create_attachment_metadata', {
    p_attachment_id: local.attachmentId,
    p_vault_id: context.device.vaultId,
    p_record_id: local.recordId,
    p_device_id: context.device.deviceId,
    p_device_auth_token: context.device.deviceAuthToken,
    p_crypto_version: local.cryptoVersion,
    p_wrapped_attachment_key: local.wrappedAttachmentKey,
    p_key_nonce: local.keyNonce,
    p_original_nonce: local.originalNonce,
    p_thumbnail_nonce: local.thumbnailNonce ?? null,
    p_ciphertext_size: local.sizeBytes,
    p_thumbnail_ciphertext_size: local.thumbnailSizeBytes ?? null,
    p_checksum: local.originalChecksum,
    p_thumbnail_checksum: local.thumbnailChecksum ?? null,
    p_mime_type: local.mimeType || 'application/octet-stream',
    p_original_name_ciphertext: null,
  });
  if (error) throw error;
  const row = (Array.isArray(data) ? data[0] : data) as AttachmentMetadataRow | null;
  return row;
}

async function uploadVariant(
  attachmentId: string,
  variant: AttachmentVariant,
  ciphertext: ArrayBuffer,
  expectedChecksum: string,
): Promise<void> {
  const signed = await signedOperation(attachmentId, 'put', variant);
  if (signed.expectedSize != null && signed.expectedSize !== ciphertext.byteLength) {
    throw new Error(`Encrypted ${variant} size does not match its authorized metadata.`);
  }
  if (signed.expectedChecksum && signed.expectedChecksum.toLowerCase() !== expectedChecksum.toLowerCase()) {
    throw new Error(`Encrypted ${variant} checksum does not match its authorized metadata.`);
  }

  const response = await fetch(signed.url, {
    method: signed.method,
    headers: transferHeaders(signed),
    body: ciphertext,
  });
  if (!response.ok) throw new Error(`Secure backup upload failed (${response.status}).`);
}

async function uploadOne(local: LocalEncryptedAttachment): Promise<void> {
  if (!context || !supabase) throw new Error('Cloud synchronization is not configured.');
  if (!local.originalCiphertext) throw new Error('The encrypted attachment bytes are no longer stored on this device.');

  const metadata = await createAttachmentMetadata(local);
  if (metadata?.state === 'uploaded') {
    await updateLocalAttachment(local.attachmentId, { state: 'uploaded', lastError: undefined });
    return;
  }
  if (metadata?.state === 'deleted' || metadata?.state === 'purged') {
    throw new Error('This attachment is already marked for deletion.');
  }

  await updateLocalAttachment(local.attachmentId, { state: 'pending', lastError: undefined });
  await uploadVariant(local.attachmentId, 'original', local.originalCiphertext, local.originalChecksum);
  if (local.thumbnailCiphertext && local.thumbnailChecksum) {
    await uploadVariant(local.attachmentId, 'thumbnail', local.thumbnailCiphertext, local.thumbnailChecksum);
  }

  const { error } = await supabase.rpc('finalize_attachment_upload', {
    p_attachment_id: local.attachmentId,
    p_device_id: context.device.deviceId,
    p_device_auth_token: context.device.deviceAuthToken,
  });
  if (error) throw error;
  await updateLocalAttachment(local.attachmentId, { state: 'uploaded', lastError: undefined });
}

async function deleteRemoteVariant(attachmentId: string, variant: AttachmentVariant): Promise<void> {
  const signed = await signedOperation(attachmentId, 'delete', variant);
  const response = await fetch(signed.url, { method: signed.method, headers: transferHeaders(signed) });
  if (!response.ok) throw new Error(`Secure backup deletion failed (${response.status}).`);
}

async function deleteOne(deletion: AttachmentDeletionRequest): Promise<void> {
  if (!context || !supabase) throw new Error('Cloud synchronization is not configured.');

  const { data: authorization, error: markError } = await supabase.rpc('mark_attachment_deleted_for_revision', {
    p_attachment_id: deletion.attachmentId,
    p_record_id: deletion.recordId,
    p_source_revision_id: deletion.sourceRevisionId,
    p_device_id: context.device.deviceId,
    p_device_auth_token: context.device.deviceAuthToken,
  });
  if (markError) throw markError;

  const status = typeof authorization === 'string' ? authorization : '';
  if (status === 'wait') {
    throw new Error('Attachment deletion is waiting for its record revision to become canonical.');
  }
  if (status === 'discard') {
    await deleteAttachmentDeletion(deletion.attachmentId);
    return;
  }
  if (status === 'local_only' || status === 'purged') {
    await deleteLocalAttachment(deletion.attachmentId);
    await deleteAttachmentDeletion(deletion.attachmentId);
    return;
  }
  if (status !== 'deleted') {
    throw new Error('Attachment deletion authorization returned an unexpected state.');
  }

  const metadata = await loadAttachmentMetadata(deletion.attachmentId);
  if (!metadata) throw new Error('Attachment metadata is not available yet.');
  if (metadata.state !== 'purged') {
    await deleteRemoteVariant(deletion.attachmentId, 'original');
    if (metadata.thumbnail_object_key) await deleteRemoteVariant(deletion.attachmentId, 'thumbnail');

    const { error: finalizeError } = await supabase.rpc('finalize_attachment_deletion', {
      p_attachment_id: deletion.attachmentId,
      p_device_id: context.device.deviceId,
      p_device_auth_token: context.device.deviceAuthToken,
    });
    if (finalizeError) throw finalizeError;
  }

  await deleteLocalAttachment(deletion.attachmentId);
  await deleteAttachmentDeletion(deletion.attachmentId);
}

export async function queueAttachmentDeletions(
  attachmentIds: Iterable<string>,
  recordId: string,
  sourceRevisionId: string,
): Promise<void> {
  for (const attachmentId of new Set(attachmentIds)) {
    await queueAttachmentDeletion(attachmentId, recordId, sourceRevisionId);
  }
}

export async function cancelAttachmentDeletions(attachmentIds: Iterable<string>): Promise<void> {
  for (const attachmentId of new Set(attachmentIds)) {
    await deleteAttachmentDeletion(attachmentId);
  }
}

async function runQueue(force: boolean): Promise<AttachmentSyncSummary> {
  const summary: AttachmentSyncSummary = { uploaded: 0, deleted: 0, failed: 0 };
  if (!context || !r2SyncEnabled || !navigator.onLine) return summary;

  const deletions = await listAttachmentDeletions();
  for (const deletion of deletions) {
    try {
      await deleteOne(deletion);
      summary.deleted += 1;
    } catch (cause) {
      summary.failed += 1;
      await updateAttachmentDeletion(deletion.attachmentId, {
        attempts: deletion.attempts + 1,
        lastError: errorMessage(cause),
      });
    }
  }

  const deletingIds = new Set((await listAttachmentDeletions()).map((entry) => entry.attachmentId));
  const attachments = await listLocalAttachments();
  for (const local of attachments) {
    if (deletingIds.has(local.attachmentId) || local.state === 'uploaded') continue;
    if (!force && local.state === 'failed' && Date.now() - Date.parse(local.updatedAt) < 30_000) continue;
    // The local attachment store is shared by every wallet on this browser. Only upload
    // photos whose item belongs to the connected wallet; skip the rest so we don't try to
    // register another wallet's photo under this vault (the server rejects it "Record not found").
    const owned = await context.database.getOptional<{ ok: number }>(
      'SELECT 1 AS ok FROM record_revisions WHERE record_id = ? AND vault_id = ? LIMIT 1',
      [local.recordId, context.device.vaultId],
    );
    if (!owned) continue;
    try {
      syncLog.info(`uploading photo ${local.attachmentId} for record ${local.recordId}, vault ${context.device.vaultId}.`);
      await uploadOne(local);
      summary.uploaded += 1;
    } catch (cause) {
      const message = errorMessage(cause);
      // The item's record has not finished syncing to the server yet (its metadata is
      // created only after the record exists). Keep it pending so it retries on the next
      // pass — every ~15s — instead of backing off as a hard failure.
      if (/record not found/i.test(message)) {
        await updateLocalAttachment(local.attachmentId, { state: 'pending', lastError: message });
        continue;
      }
      summary.failed += 1;
      await updateLocalAttachment(local.attachmentId, { state: 'failed', lastError: message });
    }
  }
  return summary;
}

export function processAttachmentSyncQueue(force = false): Promise<AttachmentSyncSummary> {
  if (queuePromise) return queuePromise;
  queuePromise = runQueue(force).finally(() => { queuePromise = null; });
  return queuePromise;
}

export async function ensureCloudAttachmentVariant(
  ref: AttachmentRef,
  recordId: string,
  preferOriginal: boolean,
): Promise<LocalEncryptedAttachment | null> {
  const existing = await loadLocalAttachment(ref.attachmentId);
  if (!context || !r2SyncEnabled || !navigator.onLine) return existing;

  const metadata = await loadAttachmentMetadata(ref.attachmentId);
  if (!metadata || metadata.state !== 'uploaded') return existing;
  if (metadata.vault_id !== context.device.vaultId || metadata.record_id !== recordId) {
    throw new Error('Attachment metadata does not match this wallet item.');
  }

  const variant: AttachmentVariant = preferOriginal || !metadata.thumbnail_object_key ? 'original' : 'thumbnail';
  if (variant === 'original' && existing?.originalCiphertext) return existing;
  if (variant === 'thumbnail' && existing?.thumbnailCiphertext) return existing;

  const signed = await signedOperation(ref.attachmentId, 'get', variant);
  const response = await fetch(signed.url, { method: signed.method, headers: transferHeaders(signed) });
  if (!response.ok) throw new Error(`Secure backup download failed (${response.status}).`);
  const ciphertext = await response.arrayBuffer();
  const expectedSize = signed.expectedSize
    ?? (variant === 'original' ? metadata.ciphertext_size : metadata.thumbnail_ciphertext_size);
  const expectedChecksum = signed.expectedChecksum
    ?? (variant === 'original' ? metadata.checksum : metadata.thumbnail_checksum);
  if (expectedSize != null && ciphertext.byteLength !== expectedSize) {
    throw new Error(`Downloaded encrypted ${variant} has an unexpected size.`);
  }
  if (expectedChecksum && await sha256Hex(ciphertext) !== expectedChecksum.toLowerCase()) {
    throw new Error(`Downloaded encrypted ${variant} failed its integrity check.`);
  }

  const timestamp = new Date().toISOString();
  const stored: LocalEncryptedAttachment = {
    attachmentId: ref.attachmentId,
    recordId,
    slot: ref.slot ?? 'note',
    cryptoVersion: 1,
    wrappedAttachmentKey: metadata.wrapped_attachment_key,
    keyNonce: metadata.key_nonce,
    originalNonce: metadata.original_nonce,
    originalChecksum: metadata.checksum,
    originalCiphertext: variant === 'original' ? ciphertext : existing?.originalCiphertext,
    thumbnailNonce: metadata.thumbnail_nonce ?? undefined,
    thumbnailChecksum: metadata.thumbnail_checksum ?? undefined,
    thumbnailCiphertext: variant === 'thumbnail' ? ciphertext : existing?.thumbnailCiphertext,
    mimeType: metadata.mime_type || ref.mimeType || 'application/octet-stream',
    sizeBytes: metadata.ciphertext_size,
    thumbnailSizeBytes: metadata.thumbnail_ciphertext_size ?? undefined,
    state: 'uploaded',
    createdAt: existing?.createdAt ?? timestamp,
    updatedAt: timestamp,
  };
  await saveLocalAttachment(stored);
  return stored;
}
